/**
 * Date helpers for the Calendar primitive — Plan 0002 §7.4.
 *
 * Pure functions over the native `Date`, all in the browser's local
 * time zone (the same posture as `apps/frontend/src/lib/format.ts`).
 * No `date-fns` dependency: the calendar needs ~15 small helpers and
 * a hand-rolled set keeps the bundle and the convention surface
 * minimal. Every function is immutable — inputs are never mutated.
 */

const MS_PER_MINUTE = 60_000;

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/**
 * Add `n` calendar months, clamping the day to the target month's
 * length so "Jan 31 + 1 month" lands on Feb 28/29, never spills into
 * March.
 */
export function addMonths(d: Date, n: number): Date {
  const x = new Date(d);
  const day = x.getDate();
  x.setDate(1);
  x.setMonth(x.getMonth() + n);
  const lastDay = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
  x.setDate(Math.min(day, lastDay));
  return x;
}

/** First moment of the week. `weekStartsOn`: 0 = Sunday (default), 1 = Monday. */
export function startOfWeek(d: Date, weekStartsOn: 0 | 1 = 0): Date {
  const x = startOfDay(d);
  const diff = (x.getDay() - weekStartsOn + 7) % 7;
  return addDays(x, -diff);
}

export function endOfWeek(d: Date, weekStartsOn: 0 | 1 = 0): Date {
  return endOfDay(addDays(startOfWeek(d, weekStartsOn), 6));
}

export function startOfMonth(d: Date): Date {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
}

export function endOfMonth(d: Date): Date {
  const x = startOfMonth(d);
  x.setMonth(x.getMonth() + 1);
  x.setDate(0);
  return endOfDay(x);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

export function isToday(d: Date): boolean {
  return isSameDay(d, new Date());
}

/** Inclusive list of day-starts from `start` to `end`. */
export function eachDayOfInterval(start: Date, end: Date): Date[] {
  const days: Date[] = [];
  let cur = startOfDay(start);
  const last = startOfDay(end);
  while (cur.getTime() <= last.getTime()) {
    days.push(cur);
    cur = addDays(cur, 1);
  }
  return days;
}

/** Minutes elapsed since midnight — used to position events in a day grid. */
export function minutesIntoDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

/** Whole minutes between two instants (b - a). */
export function minutesBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / MS_PER_MINUTE);
}

/**
 * The 6×7 (42-cell) grid that backs a month view: every day from the
 * start-of-week of the 1st through six full weeks. Always 42 cells so
 * the grid height never jumps between months.
 */
export function monthGridDays(month: Date, weekStartsOn: 0 | 1 = 0): Date[] {
  const gridStart = startOfWeek(startOfMonth(month), weekStartsOn);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

// --- formatters (Intl-backed, locale-aware) --------------------------------

export function formatMonthYear(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export function formatWeekdayShort(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: "short" });
}

export function formatDayHeading(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

/** "9 AM", "12 PM" — for the hour gutter of the day/week grid. */
export function formatHourLabel(hour: number): string {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric" });
}

export function formatTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** "Mar 3 – 9, 2026", "Mar 30 – Apr 5, 2026" or "Dec 28, 2025 – Jan 3, 2026". */
export function formatWeekRange(start: Date, end: Date): string {
  const monthDay: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  if (start.getFullYear() !== end.getFullYear()) {
    // A week spanning New Year needs both years — a single trailing
    // year would mislabel the start date's year.
    const withYear: Intl.DateTimeFormatOptions = { ...monthDay, year: "numeric" };
    return `${start.toLocaleDateString(undefined, withYear)} – ${end.toLocaleDateString(undefined, withYear)}`;
  }
  const startStr = start.toLocaleDateString(undefined, monthDay);
  const endStr = end.toLocaleDateString(
    undefined,
    start.getMonth() === end.getMonth() ? { day: "numeric" } : monthDay,
  );
  return `${startStr} – ${endStr}, ${end.getFullYear()}`;
}
